import React, { createContext, useContext, useEffect, useState, useCallback } from 'react';

const STORAGE_KEY = 'mm-theme'; // 'light' | 'dark' | absent => follow system

const ThemeContext = createContext({
  theme: 'light',
  toggleTheme: () => {},
  setTheme: () => {},
  forcedLight: false,
  enterForcedLight: () => {},
  exitForcedLight: () => {},
});

const getInitialTheme = () => {
  try {
    const stored = localStorage.getItem(STORAGE_KEY);
    if (stored === 'light' || stored === 'dark') return stored;
    return window.matchMedia('(prefers-color-scheme: dark)').matches ? 'dark' : 'light';
  } catch (e) {
    return 'light';
  }
};

export const ThemeProvider = ({ children }) => {
  const [theme, setThemeState] = useState(getInitialTheme);
  const [forcedLight, setForcedLight] = useState(false);

  useEffect(() => {
    // When a public page is active, always render light regardless of tenant preference
    const effectiveDark = theme === 'dark' && !forcedLight;
    document.documentElement.classList.toggle('dark', effectiveDark);
    document
      .querySelector('meta[name="theme-color"]')
      ?.setAttribute('content', effectiveDark ? '#07101e' : '#f8fafc');
  }, [theme, forcedLight]);

  // Follow OS preference only while the user has never explicitly chosen
  useEffect(() => {
    const mq = window.matchMedia('(prefers-color-scheme: dark)');
    const onChange = (e) => {
      try {
        if (!localStorage.getItem(STORAGE_KEY)) setThemeState(e.matches ? 'dark' : 'light');
      } catch (err) {}
    };
    mq.addEventListener('change', onChange);
    return () => mq.removeEventListener('change', onChange);
  }, []);

  const setTheme = useCallback((next) => {
    setThemeState(next);
    try {
      localStorage.setItem(STORAGE_KEY, next);
    } catch (e) {}
  }, []);

  const toggleTheme = useCallback(() => {
    setThemeState((t) => {
      const next = t === 'dark' ? 'light' : 'dark';
      try {
        localStorage.setItem(STORAGE_KEY, next);
      } catch (e) {}
      return next;
    });
  }, []);

  const enterForcedLight = useCallback(() => setForcedLight(true), []);
  const exitForcedLight = useCallback(() => setForcedLight(false), []);

  return (
    <ThemeContext.Provider value={{ theme, toggleTheme, setTheme, forcedLight, enterForcedLight, exitForcedLight }}>
      {children}
    </ThemeContext.Provider>
  );
};

export const useTheme = () => useContext(ThemeContext);
